
import { MelodyInstrument } from '../types';

export class SynthEngine {
  private noiseBuffer: AudioBuffer | null = null;

  constructor(noiseBuffer: AudioBuffer | null) {
      this.noiseBuffer = noiseBuffer;
  }

  public setNoiseBuffer(buffer: AudioBuffer) {
      this.noiseBuffer = buffer;
  }

  public getAvailableVoices(melodyStr: string): string[] {
    const voices = new Set<string>();
    const matches = melodyStr.match(/\(V\d+\)/g);
    if (matches) {
        matches.forEach(m => voices.add(m.replace(/[()]/g, '')));
    }
    return Array.from(voices).sort();
  }

  public parseMelodyString(melodyStr: string, voice?: string): (number | number[])[] {
    const lines = melodyStr.split('\n');
    const result: (number | number[])[] = [];
    
    lines.forEach(line => {
        const cleanLine = line.trim();
        if (!cleanLine) return;
        
        // If voice is specified, only parse lines for that voice OR lines without voice tag
        if (voice) {
            const voiceTag = `(${voice})`;
            if (cleanLine.includes('(V') && !cleanLine.includes(voiceTag)) return;
        }

        const data = cleanLine.replace(/\(V\d+\)/g, '').trim();
        if (!data) return;

        if (data.startsWith('[') && data.endsWith(']')) {
            const notes = data.slice(1, -1).split(',').map(n => parseFloat(n.trim())).filter(n => !isNaN(n));
            if (notes.length > 0) result.push(notes);
        } else {
            const note = parseFloat(data);
            if (!isNaN(note)) result.push(note);
        }
    });

    return result;
  }

  public synthTone(
      ctx: AudioContext, 
      freq: number, 
      startTime: number, 
      instrument: MelodyInstrument, 
      vol: number, 
      dest: AudioNode, 
      trail: boolean, 
      duration?: number, 
      pitchShift: number = 0
  ) {
      const dur = duration !== undefined ? duration : (trail ? 2.5 : 0.8);
      
      // Apply pitch shift to frequency
      const shiftedFreq = freq * Math.pow(2, pitchShift / 12);
      const freq_ = shiftedFreq;

      // Helper to create a basic oscillator with envelope
      const createOsc = (type: OscillatorType, f: number, v: number, attack: number, decay: number, sustain: number, release: number) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.type = type;
          osc.frequency.value = f;
          g.gain.setValueAtTime(0, startTime);
          g.gain.linearRampToValueAtTime(v * vol, startTime + attack);
          g.gain.exponentialRampToValueAtTime(v * vol * sustain, startTime + attack + decay);
          g.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          osc.connect(g);
          g.connect(dest);
          osc.start(startTime);
          osc.stop(startTime + dur);
          return { osc, g };
      };

      // Helper for filtered sounds
      const createFilteredOsc = (type: OscillatorType, f: number, v: number, attack: number, decay: number, sustain: number, release: number, filterType: BiquadFilterType, filterFreq: number, filterQ: number, filterEnv?: number) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.type = type;
          osc.frequency.value = f;
          filter.type = filterType;
          filter.Q.value = filterQ;
          filter.frequency.setValueAtTime(filterFreq, startTime);
          if (filterEnv) {
              filter.frequency.exponentialRampToValueAtTime(filterFreq * filterEnv, startTime + decay);
          }
          g.gain.setValueAtTime(0, startTime);
          g.gain.linearRampToValueAtTime(v * vol, startTime + attack);
          g.gain.exponentialRampToValueAtTime(v * vol * sustain, startTime + attack + decay);
          g.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          osc.connect(filter);
          filter.connect(g);
          g.connect(dest);
          osc.start(startTime);
          osc.stop(startTime + dur);
          return { osc, g, filter };
      };

      // Helper for noise-based sounds
      const createNoise = (v: number, attack: number, decay: number, sustain: number, release: number, filterType: BiquadFilterType, filterFreq: number, filterQ: number) => {
          const n = ctx.createBufferSource();
          n.buffer = this.noiseBuffer;
          const g = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          filter.type = filterType;
          filter.Q.value = filterQ;
          filter.frequency.setValueAtTime(filterFreq, startTime);
          g.gain.setValueAtTime(0, startTime);
          g.gain.linearRampToValueAtTime(v * vol, startTime + attack);
          g.gain.exponentialRampToValueAtTime(v * vol * sustain, startTime + attack + decay);
          g.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          n.connect(filter);
          filter.connect(g);
          g.connect(dest);
          n.start(startTime);
          n.stop(startTime + dur);
          return { n, filter, g };
      };

      switch (instrument) {
          case 'GRAND_PIANO':
          case 'BRIGHT_PIANO':
              createOsc('sine', freq_, 0.6, 0.005, 0.1, 0.5, 0.5);
              createOsc('triangle', freq_, 0.3, 0.01, 0.2, 0.3, 0.5);
              createOsc('sine', freq_ / 2, 0.2, 0.01, 0.3, 0.2, 0.5);
              break;
          case 'ELECTRIC_PIANO':
              createOsc('sine', freq_, 0.7, 0.01, 0.2, 0.4, 1.0);
              createOsc('sine', freq_ * 2, 0.2, 0.01, 0.1, 0.1, 0.5);
              break;
          case 'HONKY_TONK':
              createOsc('sawtooth', freq_, 0.3, 0.005, 0.1, 0.4, 0.5);
              createOsc('sawtooth', freq_ * 1.01, 0.3, 0.005, 0.1, 0.4, 0.5);
              break;
          case 'HARPSICHORD':
              createOsc('sawtooth', freq_, 0.5, 0.002, 0.05, 0.1, 0.1);
              createOsc('sawtooth', freq_ * 2, 0.2, 0.002, 0.05, 0.05, 0.1);
              break;
          case 'CLAVINET':
              createFilteredOsc('sawtooth', freq_, 0.6, 0.005, 0.1, 0.2, 0.2, 'lowpass', freq_ * 2, 5, 0.5);
              break;
          case 'CELESTA':
          case 'GLOCKENSPIEL':
          case 'MUSIC_BOX':
              createOsc('sine', freq_, 0.8, 0.005, 0.1, 0.1, 2.0);
              createOsc('sine', freq_ * 4, 0.2, 0.005, 0.05, 0.05, 1.0);
              break;
          case 'VIBRAPHONE':
              createOsc('sine', freq_, 0.8, 0.02, 0.1, 0.5, 2.0);
              break;
          case 'MARIMBA':
          case 'XYLOPHONE':
              createOsc('triangle', freq_, 0.8, 0.005, 0.05, 0.01, 0.2);
              break;
          case 'TUBULAR_BELLS':
              createOsc('sine', freq_, 0.6, 0.01, 0.1, 0.4, 4.0);
              createOsc('sine', freq_ * 2.5, 0.3, 0.01, 0.1, 0.2, 3.0);
              break;
          case 'DULCIMER':
              createOsc('sawtooth', freq_, 0.4, 0.005, 0.1, 0.1, 0.5);
              break;
          case 'DRAWBAR_ORGAN':
          case 'PERCUSSIVE_ORGAN':
          case 'ROCK_ORGAN':
          case 'CHURCH_ORGAN':
          case 'REED_ORGAN':
              createOsc('sine', freq_, 0.5, 0.05, 0.1, 0.8, 0.2);
              createOsc('sine', freq_ * 2, 0.3, 0.05, 0.1, 0.6, 0.2);
              createOsc('sine', freq_ * 0.5, 0.2, 0.05, 0.1, 0.7, 0.2);
              break;
          case 'ACCORDION':
          case 'TANGO_ACCORDION':
          case 'HARMONICA':
              createOsc('sawtooth', freq_, 0.4, 0.1, 0.1, 0.8, 0.2);
              createOsc('sawtooth', freq_ * 1.01, 0.3, 0.1, 0.1, 0.8, 0.2);
              break;
          case 'NYLON_GUITAR':
          case 'STEEL_GUITAR':
          case 'JAZZ_GUITAR':
          case 'CLEAN_GUITAR':
              createFilteredOsc('sawtooth', freq_, 0.6, 0.01, 0.1, 0.3, 0.8, 'lowpass', freq_ * 4, 1, 0.25);
              break;
          case 'MUTED_GUITAR':
              createFilteredOsc('sawtooth', freq_, 0.5, 0.005, 0.05, 0.01, 0.1, 'lowpass', freq_ * 2, 2, 0.1);
              break;
          case 'OVERDRIVEN_GUITAR':
          case 'DISTORTION_GUITAR':
              createFilteredOsc('sawtooth', freq_, 0.6, 0.01, 0.1, 0.8, 0.5, 'lowpass', freq_ * 3, 2);
              break;
          case 'GUITAR_HARMONICS':
              createOsc('sine', freq_ * 2, 0.6, 0.005, 0.1, 0.1, 1.5);
              break;
          case 'ACOUSTIC_BASS':
          case 'FINGER_BASS':
          case 'PICK_BASS':
              createFilteredOsc('triangle', freq_, 0.8, 0.01, 0.1, 0.4, 0.5, 'lowpass', freq_ * 2, 1);
              break;
          case 'FRETLESS_BASS':
              createOsc('sine', freq_, 0.8, 0.05, 0.2, 0.6, 0.8);
              break;
          case 'SLAP_BASS_1':
          case 'SLAP_BASS_2':
              createFilteredOsc('square', freq_, 0.6, 0.005, 0.05, 0.2, 0.2, 'lowpass', freq_ * 5, 5, 0.2);
              break;
          case 'SYNTH_BASS_1':
          case 'SYNTH_BASS_2':
          case 'SYNTH_BASS_3':
          case 'SYNTH_BASS_4':
              createFilteredOsc('sawtooth', freq_, 0.7, 0.01, 0.2, 0.5, 0.5, 'lowpass', freq_ * 2, 2, 0.5);
              break;
          case 'VIOLIN':
          case 'VIOLA':
          case 'CELLO':
          case 'CONTRABASS':
              createOsc('sawtooth', freq_, 0.4, 0.1, 0.2, 0.6, 0.5);
              createOsc('sine', freq_, 0.3, 0.1, 0.2, 0.6, 0.5);
              break;
          case 'TREMOLO_STRINGS':
              createOsc('sawtooth', freq_, 0.4, 0.05, 0.1, 0.6, 0.2);
              break;
          case 'PIZZICATO_STRINGS':
              createOsc('sawtooth', freq_, 0.7, 0.005, 0.05, 0.01, 0.1);
              break;
          case 'ORCHESTRAL_HARP':
              createOsc('sine', freq_, 0.7, 0.01, 0.1, 0.2, 1.5);
              break;
          case 'TIMPANI':
              createFilteredOsc('sine', freq_, 0.9, 0.005, 0.1, 0.01, 0.5, 'lowpass', freq_ * 2, 1);
              break;
          case 'STRING_ENSEMBLE_1':
          case 'STRING_ENSEMBLE_2':
          case 'SYNTH_STRINGS_1':
          case 'SYNTH_STRINGS_2':
              createOsc('sawtooth', freq_, 0.3, 0.2, 0.5, 0.7, 1.0);
              createOsc('sawtooth', freq_ * 1.005, 0.2, 0.2, 0.5, 0.7, 1.0);
              break;
          case 'CHOIR_AAHS':
          case 'VOICE_OOHS':
          case 'SYNTH_VOICE':
          case 'CHOIR_PAD':
              createFilteredOsc('sawtooth', freq_, 0.4, 0.3, 0.5, 0.8, 1.0, 'lowpass', freq_ * 2, 1);
              break;
          case 'ORCHESTRA_HIT':
              createOsc('sawtooth', freq_, 0.8, 0.01, 0.1, 0.1, 0.1);
              createOsc('square', freq_ * 1.5, 0.4, 0.01, 0.1, 0.1, 0.1);
              break;
          case 'TRUMPET':
          case 'TROMBONE':
          case 'TUBA':
          case 'FRENCH_HORN':
          case 'BRASS_SECTION':
              createFilteredOsc('sawtooth', freq_, 0.5, 0.05, 0.1, 0.7, 0.2, 'lowpass', freq_ * 3, 1, 0.8);
              break;
          case 'MUTED_TRUMPET':
              createFilteredOsc('sawtooth', freq_, 0.4, 0.05, 0.1, 0.6, 0.2, 'bandpass', freq_ * 2, 5);
              break;
          case 'SYNTH_BRASS_1':
          case 'SYNTH_BRASS_2':
              createFilteredOsc('sawtooth', freq_, 0.6, 0.05, 0.2, 0.7, 0.3, 'lowpass', freq_ * 4, 2, 0.5);
              break;
          case 'SOPRANO_SAX':
          case 'ALTO_SAX':
          case 'TENOR_SAX':
          case 'BARITONE_SAX':
          case 'OBOE':
          case 'ENGLISH_HORN':
          case 'BASSOON':
          case 'CLARINET':
              createFilteredOsc('sawtooth', freq_, 0.5, 0.05, 0.1, 0.8, 0.2, 'lowpass', freq_ * 2, 1);
              break;
          case 'PICCOLO':
          case 'FLUTE':
          case 'RECORDER':
          case 'PAN_FLUTE':
          case 'WHISTLE':
          case 'OCARINA':
              createOsc('sine', freq_, 0.7, 0.05, 0.1, 0.8, 0.3);
              // Add some noise for breath
              const n = ctx.createBufferSource();
              n.buffer = this.noiseBuffer;
              const ng = ctx.createGain();
              ng.gain.setValueAtTime(0, startTime);
              ng.gain.linearRampToValueAtTime(0.05 * vol, startTime + 0.05);
              ng.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
              n.connect(ng); ng.connect(dest);
              n.start(startTime); n.stop(startTime + dur);
              break;
          case 'BLOWN_BOTTLE':
              createFilteredOsc('sine', freq_, 0.6, 0.1, 0.2, 0.7, 0.5, 'lowpass', freq_ * 1.5, 5);
              break;
          case 'SHAKUHACHI':
              createOsc('sine', freq_, 0.6, 0.2, 0.2, 0.7, 1.0);
              break;
          case 'SQUARE_LEAD':
              createOsc('square', freq_, 0.5, 0.01, 0.1, 0.7, 0.2);
              break;
          case 'SAWTOOTH_LEAD':
              createOsc('sawtooth', freq_, 0.5, 0.01, 0.1, 0.7, 0.2);
              break;
          case 'CALLIOPE_LEAD':
              createOsc('sine', freq_, 0.6, 0.05, 0.1, 0.8, 0.5);
              break;
          case 'CHIFF_LEAD':
              createOsc('sine', freq_, 0.5, 0.01, 0.1, 0.7, 0.2);
              break;
          case 'CHARANG_LEAD':
              createOsc('sawtooth', freq_, 0.4, 0.01, 0.1, 0.7, 0.2);
              createOsc('square', freq_, 0.2, 0.01, 0.1, 0.7, 0.2);
              break;
          case 'VOICE_LEAD':
              createFilteredOsc('sawtooth', freq_, 0.5, 0.1, 0.2, 0.8, 0.5, 'bandpass', freq_ * 2, 2);
              break;
          case 'FIFTHS_LEAD':
              createOsc('sawtooth', freq_, 0.4, 0.01, 0.1, 0.7, 0.2);
              createOsc('sawtooth', freq_ * 1.5, 0.3, 0.01, 0.1, 0.7, 0.2);
              break;
          case 'BASS_LEAD':
              createOsc('sawtooth', freq_, 0.4, 0.01, 0.1, 0.7, 0.2);
              createOsc('sawtooth', freq_ / 2, 0.3, 0.01, 0.1, 0.7, 0.2);
              break;
          case '8BIT_LEAD':
              createOsc('square', freq_, 0.5, 0.001, 0.05, 0.1, 0.1);
              break;
          case 'SYNTH_PLUCK':
              createFilteredOsc('sawtooth', freq_, 0.6, 0.005, 0.1, 0.01, 0.2, 'lowpass', freq_ * 8, 5, 0.1);
              break;
          case 'NEW_AGE_PAD':
          case 'WARM_PAD':
          case 'POLYSYNTH_PAD':
          case 'BOWED_PAD':
          case 'METALLIC_PAD':
          case 'HALO_PAD':
          case 'SWEEP_PAD':
              createOsc('sine', freq_, 0.4, 0.5, 0.5, 0.8, 1.5);
              createOsc('sawtooth', freq_, 0.1, 0.8, 0.8, 0.8, 2.0);
              break;
          case 'RAIN_FX':
          case 'SOUNDTRACK_FX':
          case 'CRYSTAL_FX':
          case 'ATMOSPHERE_FX':
          case 'BRIGHTNESS_FX':
          case 'GOBLINS_FX':
          case 'ECHOES_FX':
          case 'SCI_FI_FX':
          case 'ETHEREAL_CHIME':
              createOsc('sine', freq_, 0.5, 0.1, 0.5, 0.5, 3.0);
              createOsc('sine', freq_ * 2.5, 0.2, 0.2, 0.5, 0.3, 2.5);
              break;
          case 'SITAR':
          case 'BANJO':
          case 'SHAMISEN':
          case 'KOTO':
          case 'KALIMBA':
              createFilteredOsc('sawtooth', freq_, 0.7, 0.005, 0.1, 0.01, 0.5, 'lowpass', freq_ * 3, 5, 0.2);
              break;
          case 'BAGPIPE':
              createOsc('sawtooth', freq_, 0.4, 0.2, 0.2, 0.9, 0.5);
              createOsc('sawtooth', freq_ / 2, 0.2, 0.2, 0.2, 0.9, 0.5);
              break;
          case 'FIDDLE':
              createOsc('sawtooth', freq_, 0.5, 0.05, 0.1, 0.7, 0.3);
              break;
          case 'SHANAI':
              createFilteredOsc('sawtooth', freq_, 0.5, 0.1, 0.2, 0.8, 0.5, 'lowpass', freq_ * 2, 2);
              break;
          case 'TINKLE_BELL':
          case 'AGOGO':
              createOsc('sine', freq_, 0.8, 0.002, 0.05, 0.01, 0.5);
              break;
          case 'STEEL_DRUMS':
              createOsc('sine', freq_, 0.7, 0.01, 0.1, 0.2, 0.5);
              createOsc('sine', freq_ * 2, 0.2, 0.01, 0.1, 0.1, 0.3);
              break;
          case 'WOODBLOCK':
              createOsc('triangle', freq_, 0.8, 0.002, 0.03, 0.01, 0.05);
              break;
          case 'TAIKO_DRUM':
          case 'MELODIC_TOM':
          case 'SYNTH_DRUM':
              createFilteredOsc('triangle', freq_, 0.9, 0.005, 0.1, 0.01, 0.3, 'lowpass', freq_ * 1.5, 2);
              break;
          case 'REVERSE_CYMBAL':
              createOsc('sine', freq_, 0.6, dur - 0.1, 0.05, 1.0, 0.01);
              break;
          case 'PURE_SINE':
              createOsc('sine', freq_, 0.8, 0.01, 0.1, 0.5, 0.5);
              break;
          case 'SYNTH_LEAD_3':
              createFilteredOsc('square', freq_, 0.4, 0.01, 0.1, 0.5, 0.5, 'lowpass', freq_ * 4, 1);
              createOsc('sine', freq_ * 1.5, 0.2, 0.01, 0.1, 0.4, 0.4);
              break;
          case 'SYNTH_LEAD_4':
              createFilteredOsc('sawtooth', freq_, 0.4, 0.01, 0.1, 0.5, 0.5, 'highpass', freq_, 1);
              createOsc('sawtooth', freq_ * 1.01, 0.2, 0.01, 0.1, 0.4, 0.4);
              break;
          case 'SPACE_VOICE':
              createFilteredOsc('sine', freq_, 0.5, 0.5, 0.5, 0.5, 1.5, 'lowpass', freq_ * 2, 1);
              createNoise(0.05, 0.5, 0.5, 0.5, 1.5, 'bandpass', freq_ * 4, 10);
              break;
          case 'ALIEN_DRONE':
              createOsc('sawtooth', freq_ * 0.5, 0.3, 1.0, 1.0, 1.0, 2.0);
              createOsc('sine', freq_ * 0.51, 0.3, 1.0, 1.0, 1.0, 2.0);
              break;
          case 'THUNDER_FX':
              createNoise(0.8, 0.01, 0.5, 0.5, 2.0, 'lowpass', 200, 1);
              break;
          case 'WIND_FX':
              createNoise(0.4, 1.0, 1.0, 1.0, 3.0, 'bandpass', freq_, 1);
              break;
          case 'WATER_FX':
              createFilteredOsc('sine', freq_ * 4, 0.6, 0.005, 0.05, 0.1, 0.2, 'bandpass', freq_ * 4, 5);
              break;
          case 'CHURCH_BELL':
              createOsc('sine', freq_, 0.8, 0.005, 0.1, 0.5, 2.0);
              createOsc('sine', freq_ * 1.5, 0.4, 0.005, 0.1, 0.3, 1.5);
              createOsc('sine', freq_ * 2.1, 0.2, 0.005, 0.1, 0.2, 1.0);
              break;
          case 'HAND_BELL':
              createOsc('sine', freq_, 0.8, 0.002, 0.05, 0.2, 0.8);
              createOsc('sine', freq_ * 3, 0.4, 0.002, 0.05, 0.1, 0.4);
              break;
          case 'ORCHESTRA_HIT_2':
              createOsc('sawtooth', freq_, 0.5, 0.01, 0.05, 0.1, 0.2);
              createOsc('square', freq_ * 1.01, 0.3, 0.01, 0.05, 0.1, 0.2);
              createOsc('sine', freq_ * 0.5, 0.5, 0.01, 0.05, 0.1, 0.2);
              break;
          case 'ORCHESTRA_HIT_3':
              createOsc('sawtooth', freq_, 0.4, 0.01, 0.05, 0.1, 0.3);
              createOsc('square', freq_ * 1.5, 0.2, 0.01, 0.05, 0.1, 0.3);
              createOsc('sawtooth', freq_ * 0.5, 0.4, 0.01, 0.05, 0.1, 0.3);
              break;
          case 'BIRD_TWEET':
              createOsc('sine', freq_ * 4, 0.4, 0.01, 0.05, 0.1, 0.1);
              createOsc('sine', freq_ * 6, 0.2, 0.01, 0.05, 0.1, 0.1);
              break;
          case 'TELEPHONE_RING':
              createOsc('square', freq_, 0.5, 0.01, 0.05, 0.1, 0.5);
              createOsc('square', freq_ * 1.2, 0.4, 0.01, 0.05, 0.1, 0.5);
              break;
          case 'HELICOPTER':
              createNoise(0.6, 0.1, 0.1, 0.1, 1.0, 'lowpass', 100, 1);
              break;
          case 'APPLAUSE':
              createNoise(0.5, 0.1, 0.1, 0.1, 1.0, 'bandpass', 2000, 1);
              break;
          case 'GUNSHOT':
              createNoise(1.0, 0.001, 0.05, 0.01, 0.2, 'lowpass', 1000, 1);
              break;
          default:
              createOsc('sine', freq_, 0.8, 0.01, 0.1, 0.5, 0.5);
      }
  }
}
